import type { ChangeEvent, FocusEvent } from 'react';
import { forwardRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { Check } from '@elfsight/icons';
import { getColorWithAlpha } from '@elfsight/lib-utils';
import {
  createReadableClass,
  getInvertedColor,
  roundTo4
} from '@elfsight/widget-utils';
import { commonFieldsSettings } from '../commonFieldSettings';
import { LINE_HEIGHT_RATIO } from '../constants';

export interface ChoiceOptionProps {
  value: string;
  id: string;
  checked?: boolean;
  required?: boolean;
  name?: string;
  inputType?: string;
  fieldActiveColor?: string;
  fieldActiveFillColor?: string;
  fieldColor?: string;
  fieldFillColor: string;
  fieldFontSize: number;
  fieldBorderType?: 'outline' | 'underline' | null;
  fieldBorderColor?: string;
  fieldBorderWidth?: number;
  fieldSpacing: number;
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
}

export const ChoiceOption = forwardRef<HTMLInputElement, ChoiceOptionProps>(
  function ChoiceOption(
    {
      value,
      id,
      checked = false,
      required,
      name,
      inputType = 'radio',
      fieldActiveColor = commonFieldsSettings.fieldActiveColor,
      fieldActiveFillColor,
      fieldFillColor = commonFieldsSettings.fieldFillColor,
      fieldColor = commonFieldsSettings.fieldColor,
      fieldFontSize,
      fieldBorderType = commonFieldsSettings.fieldBorderType,
      fieldBorderColor = commonFieldsSettings.fieldBorderColor,
      fieldBorderWidth = commonFieldsSettings.fieldBorderWidth,
      fieldSpacing = commonFieldsSettings.fieldSpacing,
      onFocus,
      onChange
    },
    forwardingRef
  ) {
    const [focused, setFocused] = useState(false);
    const inputSize = Math.round(fieldFontSize * 1.2);

    const controlProps = {
      _activeColor: fieldActiveColor,
      _activeFillColor: fieldActiveFillColor,
      _size: inputSize,
      _border: fieldBorderType ? fieldBorderType : undefined,
      _borderColor: fieldBorderColor,
      _borderWidth: fieldBorderWidth,
      _textColor: fieldColor,
      _fillColor:
        fieldFillColor === 'transparent'
          ? getColorWithAlpha(fieldColor, 0.1)
          : fieldFillColor
    };

    const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
      setFocused(true);
      onFocus?.(e);
    };

    const handleBlur = () => {
      setFocused(false);
    };

    return (
      <Label _spacing={fieldSpacing} _checked={checked}>
        <Input
          _size={inputSize}
          _activeColor={fieldActiveColor}
          ref={forwardingRef}
          id={id}
          type={inputType}
          value={value}
          name={name}
          required={required}
          checked={checked}
          onChange={onChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          style={{
            width: 0,
            height: 0,
            margin: 0,
            padding: 0,
            appearance: 'none',
            overflow: 'hidden'
          }}
        />
        <Container _size={inputSize}>
          {inputType === 'radio' ? (
            <RadioControl {...controlProps} />
          ) : (
            <ChoiceControl {...controlProps}>
              <Check />
            </ChoiceControl>
          )}

          <Overlay
            _focused={focused}
            style={{
              backgroundColor: fieldColor
            }}
          />
        </Container>
        <ItemLabel
          style={{
            fontSize: fieldFontSize,
            lineHeight: `${roundTo4(
              fieldFontSize * 1.2 * LINE_HEIGHT_RATIO
            )}px`,
            marginLeft: roundTo4(fieldFontSize / 2)
          }}
          dangerouslySetInnerHTML={{ __html: value }}
        />
      </Label>
    );
  }
);

const Label = styled.label.attrs<{
  _spacing: number;
  _checked: boolean;
}>(({ _checked }) => ({
  className: createReadableClass({
    component: 'choice-option',
    element: 'label',
    index: _checked && 'checked'
  })
}))`
  ${({ _spacing }) => `
    display: flex;
    cursor: pointer;
    &:not(:last-child) {
      margin: 0 ${roundTo4(_spacing / 3)}px ${roundTo4(_spacing / 4)}px 0;
    }
  `}
`;

const Container = styled.div.attrs<{
  _size: number;
}>({
  className: createReadableClass({
    component: 'choice-option',
    element: 'container'
  })
})`
  position: relative;
  ${({ _size }) =>
    `
      top: ${Math.round((roundTo4(_size * LINE_HEIGHT_RATIO) - _size) / 2)}px;
      min-width: ${_size}px;
      height: ${_size}px;
    `}
`;

const Overlay = styled.div.attrs<{
  _focused: boolean;
}>({
  className: createReadableClass({
    component: 'choice-option',
    element: 'overlay'
  })
})`
  opacity: 0;
  width: 100%;
  height: 100%;
  position: absolute;

  ${Label}:hover & {
    opacity: 0.1;
  }

  ${({ _focused }) =>
    _focused &&
    css`
      opacity: 0.1;
    `}
`;

const ItemLabel = styled.div.attrs({
  className: createReadableClass({
    component: 'choice-option',
    element: 'item-label'
  })
})``;

const Item = styled.div.attrs<{
  _activeColor: string;
  _activeFillColor?: string;
  _size: number;
  _border?: string;
  _borderColor?: string;
  _borderWidth: number;
  _textColor: string;
  _fillColor: string;
}>({
  className: createReadableClass({
    component: 'choice-option',
    element: 'item'
  })
})`
  ${({
    _size,
    _textColor,
    _border,
    _borderColor,
    _borderWidth,
    _fillColor
  }) => `
    position: absolute;
    background: ${_fillColor};
    background-clip: padding-box;
    padding: ${(_size - 2 * (_border ? _borderWidth : 1)) / 2}px;
    border: ${_border ? _borderWidth : 1}px
      solid
      ${_border ? _borderColor : getColorWithAlpha(_textColor, 0.5)};

    &:empty {
      display: block;
    }
  `}
`;

const RadioControl = styled(Item).attrs({
  className: createReadableClass({
    component: 'choice-option',
    element: 'radio-control'
  })
})`
  &,
  &:after,
  & ~ ${Overlay} {
    border-radius: 50%;
  }

  &:after {
    content: '';
    display: none;
    position: absolute;
    transform: translate3d(-50%, -50%, 0);
    background: ${({ _activeColor, _activeFillColor }) =>
      _activeFillColor || getInvertedColor(_activeColor)};
    width: ${({ _size }) => _size / 2}px;
    height: ${({ _size }) => _size / 2}px;
  }
`;

const ChoiceControl = styled(Item).attrs({
  className: createReadableClass({
    component: 'choice-option',
    element: 'choice-control'
  })
})`
  display: flex;
  align-items: center;
  justify-content: center;

  &,
  & ~ ${Overlay} {
    border-radius: 3px;
  }

  & svg {
    display: none;
    position: absolute;
    fill: ${({ _activeColor, _activeFillColor }) =>
      _activeFillColor || getInvertedColor(_activeColor)};
    width: ${({ _size }) => roundTo4(_size * 0.75)}px;
    height: ${({ _size }) => roundTo4(_size * 0.75)}px;
  }
`;

const Input = styled.input.attrs<{
  _size: number;
  _activeColor: string;
}>({
  className: createReadableClass({
    component: 'choice-option',
    element: 'input'
  })
})`
  &:checked ~ ${Container} {
    ${Item} {
      border: none;
      padding: ${({ _size }) => _size / 2}px;
      background-color: ${({ _activeColor }) => _activeColor};

      &:after {
        display: block;
      }

      & svg {
        display: block;
      }
    }
  }
`;
